import {
  Component,
  HostBinding,
  Input,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseWidget } from '../base-widget/base-widget';
import { PopupPositionEnum } from '@app/core/enums/popup-view-type.enum';
import { ActionEnum } from '@app/core/dto/abstract-action.dto';
import { StringUtils } from '@app/core/utils/string.utils';
import {
  buildGridSlotForUserGallery,
  getReviewTextForPreview,
  hydrateUserGalleryFromWidgetData,
  isReviewExpandedForPreview,
  shouldShowReviewToggleForPreview,
  toggleReviewTextStateForPreview,
} from '../utils/review-media.util';

@Component({
  selector: 'composed-rating-review',
  templateUrl: './composed-rating-review.component.html',
  styleUrl: './composed-rating-review.component.scss',
  standalone: true,
  imports: [CommonModule],
})
export class ComposedRatingReviewComponent
  extends BaseWidget
  implements OnInit
{
  histogramCSSStylesMap: Map<number, any> = new Map<number, any>();

  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  @Input() slot: any;
  showAllReviews: WritableSignal<boolean> = signal(false);
  StringUtils = StringUtils;

  isLoggedIn: Signal<boolean> = computed(() => {
    return this.inputUserSession()?.isLoggedIn ?? false;
  });

  data: any;
  userGalleryImages: any[] = [];
  private userGallerySlot: any;
  reviewPreviewCharLimit: WritableSignal<number> = signal(0);
  private readonly reviewKeyPrefix: string = 'review-';
  expandedReviewState: Record<string, boolean> = {};
  userImagesText: string = '';
  allReviews: any[] = [];
  ngOnInit(): void {
    this.slot?.widget?.data?.ratingData?.rating?.value?.histogram.forEach(
      (e: any, i: number) => {
        const widgetAttributes = {
          ...e.widgetAttributes,
          width: Math.min(Number(e.widgetAttributes?.width) || 0, 100),
        };
        this.histogramCSSStylesMap.set(i, this.getStyles(widgetAttributes));
      },
    );

    this.data = this.slot?.widget?.data;
    this.allReviews = this.data?.reviewData?.renderableComponents || [];
    this.reviewPreviewCharLimit.set(
      this.data?.reviewData?.reviewPreviewCharLimit,
    );
    this.hydrateUserGallery();
  }

  getFloorVal(v): number {
    return Math.floor(v);
  }

  showAll() {
    this.showAllReviews.set(true);
  }

  onActionCall(action: any) {
    this.actionEvent({
      actionData: { ...action },
    });
  }

  initiateReview() {
    this.onActionCall({
      type: ActionEnum.INTIATE_REVIEW_FROM_PDP,
      params: {
        sku: this.slot?.widget?.data?.reviewData?.productInfo?.sku,
      },
    });
  }

  private hydrateUserGallery() {
    const { userGallerySlot, userImagesText, userGalleryImages } =
      hydrateUserGalleryFromWidgetData(this.data);

    this.userGallerySlot = userGallerySlot;
    this.userImagesText = userImagesText;
    this.userGalleryImages = userGalleryImages;
  }

  launchMultimediaPopup(
    selectedIndex: number,
    isGridModal?: boolean,
    reviewSlot?: any,
  ) {
    let slot: any = reviewSlot || this.userGallerySlot;

    if (isGridModal) {
      const gridSlot = buildGridSlotForUserGallery({
        userGallerySlot: this.userGallerySlot,
        userImagesText: this.userImagesText,
        userGalleryImages: this.userGalleryImages,
      });

      if (!gridSlot) {
        return;
      }

      slot = gridSlot;
    }

    const widgetType: string = slot?.widget?.type;

    this.showPopup(
      {
        data: {
          slot,
          viewType: 'POPUP',
          selected: selectedIndex,
        },
      },
      widgetType,
      PopupPositionEnum.CENTER,
    );
  }

  getReviewKey(review: any, index: number): string {
    return String(
      review?.value?.id ??
        StringUtils.format('{0}{1}', this.reviewKeyPrefix, index.toString()),
    );
  }

  shouldShowReviewToggle(text?: string): boolean {
    return shouldShowReviewToggleForPreview(
      text,
      this.reviewPreviewCharLimit(),
    );
  }

  isReviewExpanded(key: string): boolean {
    return isReviewExpandedForPreview(key, this.expandedReviewState);
  }

  getReviewText(text: string, key: string): string {
    return getReviewTextForPreview(
      text,
      key,
      this.reviewPreviewCharLimit(),
      this.expandedReviewState,
    );
  }

  toggleReviewText(key: string, event?: Event) {
    toggleReviewTextStateForPreview(key, this.expandedReviewState);
  }

  getReviewMedia(review: any): any[] {
    const media: any[] =
      review?.value?.multimedia?.data?.slot?.widget?.data?.multimediaComponents;
    return StringUtils.isEmpty(media) ? [] : media;
  }

  get visibleReviews(): any[] {
    const allReviews: any[] = this.allReviews || [];

    if (!this.slot?.widget?.data?.pdpReview && !this.showAllReviews()) {
      return allReviews.slice(0, 5);
    }

    return allReviews;
  }
}
